type PaginationItem = number | "...";

type PaginationProps = {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  className?: string;
};

const getPageRange = (current: number, total: number): PaginationItem[] => {
  const delta = 2;
  const range: PaginationItem[] = [];

  const left = Math.max(2, current - delta);
  const right = Math.min(total - 1, current + delta);

  range.push(1);

  if (left > 2) {
    range.push("...");
  }

  for (let i = left; i <= right; i++) {
    range.push(i);
  }

  if (right < total - 1) {
    range.push("...");
  }

  if (total > 1) {
    range.push(total);
  }

  return range;
};

const navButtonClass =
  "border rounded transition-colors cursor-pointer hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent";

const Pagination = ({ currentPage, totalPages, onPageChange, className }: PaginationProps) => {
  const pageItems = getPageRange(currentPage, totalPages);
  const currentIndex = pageItems.indexOf(currentPage);

  return (
    <div className={`mt-14 ${className ?? ""}`.trim()}>
      <div className="flex sm:hidden justify-center items-center gap-3">
        <button
          disabled={currentPage === 1}
          onClick={() => onPageChange(currentPage - 1)}
          className={`w-9 h-9 ${navButtonClass}`}
        >
          ‹
        </button>
        <span className="text-sm text-gray-600">
          {currentPage} / {totalPages}
        </span>
        <button
          disabled={currentPage === totalPages}
          onClick={() => onPageChange(currentPage + 1)}
          className={`w-9 h-9 ${navButtonClass}`}
        >
          ›
        </button>
      </div>

      <div className="hidden sm:flex justify-center gap-2 items-center">
        <button
          disabled={currentPage === 1}
          onClick={() => onPageChange(1)}
          className={`w-10 h-10 ${navButtonClass}`}
        >
          «
        </button>

        <button
          disabled={currentPage === 1}
          onClick={() => onPageChange(currentPage - 1)}
          className={`w-10 h-10 ${navButtonClass}`}
        >
          ‹
        </button>

        {pageItems.map((p, index) =>
          p === "..." ? (
            <button
              key={`dots-${index}`}
              onClick={() => {
                if (index < currentIndex) {
                  onPageChange(Math.max(1, currentPage - 5));
                } else {
                  onPageChange(Math.min(totalPages, currentPage + 5));
                }
              }}
              className={`w-10 h-10 text-gray-500 ${navButtonClass}`}
            >
              …
            </button>
          ) : (
            <button
              key={p}
              onClick={() => onPageChange(p as number)}
              className={`w-10 h-10 border rounded transition-colors cursor-pointer ${currentPage === p ? "bg-primary-600 text-white" : "hover:bg-gray-100"}`}
            >
              {p}
            </button>
          )
        )}

        <button
          disabled={currentPage === totalPages}
          onClick={() => onPageChange(currentPage + 1)}
          className={`w-10 h-10 ${navButtonClass}`}
        >
          ›
        </button>

        <button
          disabled={currentPage === totalPages}
          onClick={() => onPageChange(totalPages)}
          className={`w-10 h-10 ${navButtonClass}`}
        >
          »
        </button>
      </div>
    </div>
  );
};

export default Pagination;
